import { useState, useEffect, useCallback } from 'react';
import { formatFechaJornada } from '../../utils/fechaUtils';
import { Container, Row, Col, Button, Alert, Spinner, Badge, Nav, Tab, Form } from 'react-bootstrap';
import { FaUsers, FaArrowLeft, FaEye, FaCalendarAlt, FaMapMarkerAlt, FaTrophy, FaClock, FaCheckCircle, FaChevronLeft, FaChevronRight, FaUserSlash, FaBell, FaEnvelope, FaFutbol, FaExclamationTriangle, FaRobot, FaEdit, FaFileExcel, FaUserPlus, FaPlus } from 'react-icons/fa';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import torneoService from '../../services/torneoService';
import jornadaService from '../../services/jornadaService';
import notificationService from '../../services/notificationService';
import futbolistaEnEquipoService from '../../services/futbolistaEnEquipoService';
import { toast } from 'react-toastify';
import OrganizedMatchesView from '../../components/admin/OrganizedMatchesView';
import ProposedMatchesView from '../../components/admin/ProposedMatchesView';

import EditarJornadaInline from '../../components/admin/EditarJornadaInline';
import * as XLSX from 'xlsx';
import './TournamentPlayers.css';

/**
 * Página para mostrar futbolistas inscritos en un torneo específico
 * 
 * Esta página permite a los administradores:
 * - Ver todos los futbolistas inscritos en un torneo
 * - Ver información básica (nombre, posición, fecha de nacimiento)
 * - Ver detalles completos de cada futbolista
 * - Navegar de vuelta a la página anterior
 */
const TournamentPlayers = () => {
  const { torneoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Obtener información del torneo y ruta de retorno del state
  const { torneo: torneoInfo, returnPath } = location.state || {};

  // Estados principales
  const [futbolistas, setFutbolistas] = useState([]);
  const [jornadas, setJornadas] = useState([]);
  const [torneo, setTorneo] = useState(torneoInfo || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('jornadas'); // Cambiado a 'jornadas' por defecto
  const [selectedJornada, setSelectedJornada] = useState(null);
  const [showJornadaDetail, setShowJornadaDetail] = useState(false);
  const [currentJornadaPage, setCurrentJornadaPage] = useState(1);
  const jornadasPerPage = 3;

  // Paginación para futbolistas
  const [currentFutbolistaPage, setCurrentFutbolistaPage] = useState(1);
  const futbolistasPerPage = 15;

  // Estados para futbolistas no inscritos
  const [futbolistasNoInscritos, setFutbolistasNoInscritos] = useState([]);
  const [showNoInscritos, setShowNoInscritos] = useState(false);
  const [loadingNoInscritos, setLoadingNoInscritos] = useState(false);

  // Estados para notificaciones
  const [sendingNotification, setSendingNotification] = useState(false);

  // Estados para partidos organizados
  const [partidosOrganizados, setPartidosOrganizados] = useState([]);
  const [showPartidosOrganizados, setShowPartidosOrganizados] = useState(false);
  const [loadingPartidos, setLoadingPartidos] = useState(false);

  const [limpiandoPartidos, setLimpiandoPartidos] = useState(false);

  // Estados para propuestas de partidos (Organizador Simplificado)
  const [propuestasPartidos, setPropuestasPartidos] = useState([]);
  const [showPropuestas, setShowPropuestas] = useState(false);
  const [loadingPropuestas, setLoadingPropuestas] = useState(false);
  const [proponiendoPartidos, setProponiendoPartidos] = useState(false);

  // (ELIMINADO) Estados para futbolistas no asignados

  // Estados para modal de asignación
  const [showAsignacionModal, setShowAsignacionModal] = useState(false);
  const [futbolistaSeleccionado, setFutbolistaSeleccionado] = useState(null);
  const [equiposDisponibles, setEquiposDisponibles] = useState([]);
  const [_loadingEquipos, _setLoadingEquipos] = useState(false);
  const [asignandoJugador, setAsignandoJugador] = useState(false);
  const [posicionesDisponibles, setPosicionesDisponibles] = useState([]);
  const [posicionSeleccionada, setPosicionSeleccionada] = useState(null);
  const [loadingPosiciones, setLoadingPosiciones] = useState(false);

  // Estados para modal de editar jornada
  const [showEditarJornadaModal, setShowEditarJornadaModal] = useState(false);
  const [jornadaParaEditar, setJornadaParaEditar] = useState(null);

  // Función para cargar todos los datos
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔄 Cargando datos para torneo:', torneoId);

      // Cargar futbolistas y jornadas en paralelo
      const [futbolistasResult, jornadasResult] = await Promise.all([
        torneoService.getFutbolistasEnTorneo(torneoId),
        torneoService.getJornadasConFutbolistas(torneoId)
      ]);

      if (futbolistasResult.success) {
        console.log('✅ Futbolistas cargados:', futbolistasResult.data);
        if (futbolistasResult.data.length > 0) {
          console.log('🔍 PRIMER FUTBOLISTA:', futbolistasResult.data[0]);
          console.log('🔍 posicion_principal:', futbolistasResult.data[0].posicion_principal);
          console.log('🔍 posicion_secundaria:', futbolistasResult.data[0].posicion_secundaria);

          // Buscar un futbolista con posiciones para debug
          const futbolistaConPosiciones = futbolistasResult.data.find(f => f.posicion_principal || f.posicion_secundaria);
          if (futbolistaConPosiciones) {
            console.log('🔍 FUTBOLISTA CON POSICIONES:', futbolistaConPosiciones);
          } else {
            console.log('⚠️ NO SE ENCONTRARON FUTBOLISTAS CON POSICIONES');
          }
        }
        setFutbolistas(futbolistasResult.data);
        if (futbolistasResult.torneo) {
          setTorneo(futbolistasResult.torneo);
        }
      } else {
        console.error('❌ Error cargando futbolistas:', futbolistasResult.error);
      }

      if (jornadasResult.success) {
        console.log('✅ Jornadas cargadas:', jornadasResult.data);
        setJornadas(jornadasResult.data);
        if (jornadasResult.torneo && !torneo) {
          setTorneo(jornadasResult.torneo);
        }
      } else {
        console.error('❌ Error cargando jornadas:', jornadasResult.error);
      }

      // Si ambos fallan, mostrar error
      if (!futbolistasResult.success && !jornadasResult.success) {
        setError('Error al cargar los datos del torneo');
      }

    } catch (err) {
      console.error('❌ Error cargando datos:', err);
      setError(err.message || 'Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  }, [torneoId]);

  // Cargar datos iniciales
  useEffect(() => {
    if (torneoId) {
      loadData();
    }
  }, [torneoId, loadData]);

  // Función para volver a la página anterior
  const handleGoBack = () => {
    if (returnPath) {
      navigate(returnPath);
    } else {
      navigate('/admin/matches');
    }
  };

  // Función para abrir modal de editar jornada
  const handleEditarJornada = (jornada) => {
    // Preparar datos de la jornada para el modal
    const jornadaParaEditar = {
      jornada_id: jornada.jornada_id,
      jornada_numero: jornada.jornada_numero,
      jornada_fecha: jornada.jornada_fecha,
      jornada_hora: jornada.jornada_hora,
      sede_en_torneo_id: jornada.sede_en_torneo_id,
      sede_nombre: jornada.sede_nombre,
      sede_poblacion: jornada.sede_poblacion,
      torneo_id: torneo?.id,
      torneo_nombre: torneo?.nombre,
      disputada: jornada.disputada
    };

    setJornadaParaEditar(jornadaParaEditar);
    setShowEditarJornadaModal(true);
  };

  // Función para manejar cuando se edita una jornada exitosamente
  const handleJornadaEditada = () => {
    // Recargar datos para reflejar los cambios
    loadData();

    // Cerrar modal
    setShowEditarJornadaModal(false);
    setJornadaParaEditar(null);

    toast.success('✅ Jornada editada correctamente. Los futbolistas han sido notificados.', {
      position: "top-right",
      autoClose: 4000
    });
  };

  // Función para formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'No especificada';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Función para calcular edad
  const calculateAge = (birthDate) => {
    if (!birthDate) return 'N/A';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Función para generar resumen de inscritos de una jornada específica
  const generateJornadaInscritosResumen = (jornada) => {
    if (!jornada.futbolistas_inscritos || jornada.futbolistas_inscritos.length === 0) {
      return {
        total: 0,
        porAno: {},
        porAnoPosicion: {}
      };
    }

    const resumen = {
      total: jornada.futbolistas_inscritos.length,
      porAno: {},
      porAnoPosicion: {}
    };

    jornada.futbolistas_inscritos.forEach(futbolista => {
      // Usar año de nacimiento directamente
      const anoNacimiento = futbolista.futbolista_fecha_nacimiento
        ? new Date(futbolista.futbolista_fecha_nacimiento).getFullYear()
        : 'Sin año';
      const posicion = futbolista.posicion_principal || 'Sin posición';

      // Contar por año de nacimiento
      if (!resumen.porAno[anoNacimiento]) {
        resumen.porAno[anoNacimiento] = 0;
      }
      resumen.porAno[anoNacimiento]++;

      // Contar por año y posición
      if (!resumen.porAnoPosicion[anoNacimiento]) {
        resumen.porAnoPosicion[anoNacimiento] = {};
      }
      if (!resumen.porAnoPosicion[anoNacimiento][posicion]) {
        resumen.porAnoPosicion[anoNacimiento][posicion] = 0;
      }
      resumen.porAnoPosicion[anoNacimiento][posicion]++;
    });

    return resumen;
  };

  // Función para cargar futbolistas no inscritos
  const loadFutbolistasNoInscritos = async (jornadaId) => {
    setLoadingNoInscritos(true);
    try {
      const response = await jornadaService.getFutbolistasNoInscritos(jornadaId);
      setFutbolistasNoInscritos(response.futbolistas_no_inscritos || []);
      setSelectedJornada(jornadaId);
      setShowNoInscritos(true);
    } catch (error) {
      console.error('Error al cargar futbolistas no inscritos:', error);
      setError('Error al cargar futbolistas no inscritos');
    } finally {
      setLoadingNoInscritos(false);
    }
  };

  // Función para exportar futbolistas inscritos por jornada a Excel
  const exportFutbolistasInscritosPorJornada = async () => {
    try {
      setLoading(true);

      // Obtener datos de futbolistas inscritos por jornada desde el backend
      const response = await jornadaService.getFutbolistasInscritosPorJornadaForExport(torneoId);

      if (!response.success || !response.data || response.data.length === 0) {
        toast.warning('No hay futbolistas inscritos para exportar');
        return;
      }

      const futbolistasInscritos = response.data;

      // Preparar los datos para Excel
      const excelData = futbolistasInscritos.map(futbolista => ({
        'Jornada': `J${futbolista.jornada_numero}`,
        'Fecha Jornada': futbolista.jornada_fecha ?
          formatFechaJornada(futbolista.jornada_fecha) : '',
        'Hora Jornada': futbolista.jornada_hora || '',
        'Sede': futbolista.sede_nombre || '',
        'ID Futbolista': futbolista.futbolista_id,
        'Nombre': futbolista.nombre || '',
        'Fecha Nacimiento': futbolista.fecha_nacimiento ?
          new Date(futbolista.fecha_nacimiento).toLocaleDateString('es-ES') : '',
        'Edad': futbolista.edad || '',
        'Posición Principal': futbolista.posicion_1 || '',
        'Posición Secundaria': futbolista.posicion_2 || '',
        'Categoría': futbolista.categoria || '',
        'Club': futbolista.club || '',
        'Federación Club': futbolista.club_federacion || '',
        'División': futbolista.division || '',
        'Temporada': futbolista.temporada_nombre || '',
        'Tutor': futbolista.tutor?.nombre || '',
        'Email Tutor': futbolista.tutor?.email || '',
        'Teléfono Tutor': futbolista.tutor?.telefono || '',
        'Fecha Inscripción': futbolista.fecha_inscripcion ?
          new Date(futbolista.fecha_inscripcion).toLocaleDateString('es-ES') : '',
        'Invitado': futbolista.invitado ? 'Sí' : 'No',
        'Saldo Jornadas': futbolista.saldo_jornadas || 0
      }));

      // Crear el libro de Excel
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();

      // Añadir la hoja al libro
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Futbolistas por Jornada');

      // Configurar el ancho de las columnas
      const columnWidths = [
        { wch: 8 },  // Jornada
        { wch: 12 }, // Fecha Jornada
        { wch: 8 },  // Hora Jornada
        { wch: 20 }, // Sede
        { wch: 8 },  // ID Futbolista
        { wch: 25 }, // Nombre
        { wch: 15 }, // Fecha Nacimiento
        { wch: 8 },  // Edad
        { wch: 12 }, // Posición Principal
        { wch: 12 }, // Posición Secundaria
        { wch: 15 }, // Categoría
        { wch: 20 }, // Club
        { wch: 15 }, // Federación Club
        { wch: 15 }, // División
        { wch: 12 }, // Temporada
        { wch: 25 }, // Tutor
        { wch: 30 }, // Email Tutor
        { wch: 15 }, // Teléfono Tutor
        { wch: 15 }, // Fecha Inscripción
        { wch: 8 },  // Invitado
        { wch: 8 }   // Saldo Jornadas
      ];
      worksheet['!cols'] = columnWidths;

      // Generar el nombre del archivo con fecha y hora
      const now = new Date();
      const timestamp = now.toISOString().slice(0, 19).replace(/:/g, '-');
      const torneoNombre = torneo?.nombre || 'torneo';

      // Obtener rango de jornadas para el nombre del archivo
      const jornadas = [...new Set(futbolistasInscritos.map(f => f.jornada_numero))].sort((a, b) => a - b);
      const jornadaRange = jornadas.length > 1 ?
        `jornadas_${jornadas[0]}-${jornadas[jornadas.length - 1]}` :
        `jornada_${jornadas[0] || 'todas'}`;

      const filename = `inscritos_${torneoNombre.replace(/\s+/g, '_')}_${jornadaRange}_${timestamp}.xlsx`;

      // Descargar el archivo
      XLSX.writeFile(workbook, filename);

      toast.success(`✅ Excel exportado: ${futbolistasInscritos.length} inscripciones`);

    } catch (error) {
      console.error('Error exportando futbolistas inscritos a Excel:', error);
      toast.error('Error al exportar los futbolistas inscritos a Excel');
    } finally {
      setLoading(false);
    }
  };

  // Función para exportar futbolistas inscritos de una jornada específica
  const exportFutbolistasJornadaEspecifica = async (jornadaId, jornadaNumero) => {
    try {
      setLoading(true);

      // Obtener datos de futbolistas inscritos en la jornada específica
      const response = await jornadaService.getFutbolistasInscritosJornadaEspecifica(jornadaId);

      if (!response.success || !response.data || response.data.length === 0) {
        toast.warning(`No hay futbolistas inscritos en la jornada ${jornadaNumero}`);
        return;
      }

      const futbolistasInscritos = response.data;

      // Obtener información de la jornada (todos los registros tienen la misma jornada)
      const primeraInscripcion = futbolistasInscritos[0];
      const jornadaInfo = {
        numero: primeraInscripcion.jornada_numero,
        fecha: primeraInscripcion.jornada_fecha ?
          formatFechaJornada(primeraInscripcion.jornada_fecha) : '',
        hora: primeraInscripcion.jornada_hora || '',
        sede: primeraInscripcion.sede_nombre || '',
        torneo: primeraInscripcion.torneo_nombre || ''
      };

      // Preparar los datos para Excel (incluyendo información de jornada)
      const excelData = futbolistasInscritos.map(futbolista => ({
        'Torneo': jornadaInfo.torneo,
        'Jornada': `J${jornadaInfo.numero}`,
        'Fecha Jornada': jornadaInfo.fecha,
        'Hora Jornada': jornadaInfo.hora,
        'Sede': jornadaInfo.sede,
        'ID Futbolista': futbolista.futbolista_id,
        'Nombre': futbolista.nombre || '',
        'Fecha Nacimiento': futbolista.fecha_nacimiento ?
          new Date(futbolista.fecha_nacimiento).toLocaleDateString('es-ES') : '',
        'Edad': futbolista.edad || '',
        'Posición Principal': futbolista.posicion_1 || '',
        'Posición Secundaria': futbolista.posicion_2 || '',
        'Categoría': futbolista.categoria || '',
        'Club': futbolista.club || '',
        'Federación Club': futbolista.club_federacion || '',
        'División': futbolista.division || '',
        'Temporada': futbolista.temporada_nombre || '',
        'Tutor': futbolista.tutor?.nombre || '',
        'Email Tutor': futbolista.tutor?.email || '',
        'Teléfono Tutor': futbolista.tutor?.telefono || '',
        'Fecha Inscripción': futbolista.fecha_inscripcion ?
          new Date(futbolista.fecha_inscripcion).toLocaleDateString('es-ES') : '',
        'Invitado': futbolista.invitado ? 'Sí' : 'No',
        'Saldo Jornadas': futbolista.saldo_jornadas || 0
      }));

      // Crear el libro de Excel
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();

      // Añadir la hoja al libro
      XLSX.utils.book_append_sheet(workbook, worksheet, `Jornada ${jornadaNumero}`);

      // Configurar el ancho de las columnas
      const columnWidths = [
        { wch: 20 }, // Torneo
        { wch: 8 },  // Jornada
        { wch: 12 }, // Fecha Jornada
        { wch: 8 },  // Hora Jornada
        { wch: 20 }, // Sede
        { wch: 8 },  // ID Futbolista
        { wch: 25 }, // Nombre
        { wch: 15 }, // Fecha Nacimiento
        { wch: 8 },  // Edad
        { wch: 12 }, // Posición Principal
        { wch: 12 }, // Posición Secundaria
        { wch: 15 }, // Categoría
        { wch: 20 }, // Club
        { wch: 15 }, // Federación Club
        { wch: 15 }, // División
        { wch: 12 }, // Temporada
        { wch: 25 }, // Tutor
        { wch: 30 }, // Email Tutor
        { wch: 15 }, // Teléfono Tutor
        { wch: 15 }, // Fecha Inscripción
        { wch: 8 },  // Invitado
        { wch: 8 }   // Saldo Jornadas
      ];
      worksheet['!cols'] = columnWidths;

      // Generar el nombre del archivo con fecha y hora
      const now = new Date();
      const timestamp = now.toISOString().slice(0, 19).replace(/:/g, '-');

      // Usar información real de la jornada para el nombre del archivo
      const torneoLimpio = jornadaInfo.torneo.replace(/\s+/g, '_');
      const sedeCorta = jornadaInfo.sede.replace(/\s+/g, '_').substring(0, 15);
      const fechaCorta = jornadaInfo.fecha.replace(/\//g, '-');

      const filename = `inscritos_${torneoLimpio}_jornada_${jornadaInfo.numero}_${sedeCorta}_${fechaCorta}_${timestamp}.xlsx`;

      // Descargar el archivo
      XLSX.writeFile(workbook, filename);

      toast.success(`✅ Excel exportado: ${futbolistasInscritos.length} futbolistas de la jornada ${jornadaInfo.numero} (${jornadaInfo.sede})`);

    } catch (error) {
      console.error('Error exportando futbolistas de jornada específica:', error);
      toast.error(`Error al exportar los futbolistas de la jornada ${jornadaNumero}`);
    } finally {
      setLoading(false);
    }
  };

  // Función para exportar futbolistas debutantes de una jornada específica
  const exportFutbolistasDebutantesJornadaEspecifica = async (jornadaId, jornadaNumero) => {
    try {
      setLoading(true);

      // Obtener datos de futbolistas debutantes en la jornada específica
      const response = await jornadaService.getFutbolistasDebutantesJornadaEspecifica(jornadaId);

      if (!response.success || !response.data || response.data.length === 0) {
        toast.warning(`No hay futbolistas debutantes en la jornada ${jornadaNumero}`);
        return;
      }

      const futbolistasDebutantes = response.data;

      // Obtener información de la jornada
      const primeraInscripcion = futbolistasDebutantes[0];
      const jornadaInfo = {
        numero: primeraInscripcion.jornada_numero,
        fecha: primeraInscripcion.jornada_fecha ?
          formatFechaJornada(primeraInscripcion.jornada_fecha) : '',
        hora: primeraInscripcion.jornada_hora || '',
        sede: primeraInscripcion.sede_nombre || '',
        torneo: primeraInscripcion.torneo_nombre || ''
      };

      // Preparar los datos para Excel (incluyendo talla de equipación)
      const excelData = futbolistasDebutantes.map(futbolista => ({
        'Torneo': jornadaInfo.torneo,
        'Jornada': `J${jornadaInfo.numero}`,
        'Fecha Jornada': jornadaInfo.fecha,
        'Hora Jornada': jornadaInfo.hora,
        'Sede': jornadaInfo.sede,
        'ID Futbolista': futbolista.futbolista_id,
        'Nombre': futbolista.nombre || '',
        'Fecha Nacimiento': futbolista.fecha_nacimiento ?
          new Date(futbolista.fecha_nacimiento).toLocaleDateString('es-ES') : '',
        'Edad': futbolista.edad || '',
        'Posición Principal': futbolista.posicion_1 || '',
        'Posición Secundaria': futbolista.posicion_2 || '',
        'Categoría': futbolista.categoria || '',
        'Club': futbolista.club || '',
        'Federación Club': futbolista.club_federacion || '',
        'División': futbolista.division || '',
        'Temporada': futbolista.temporada_nombre || '',
        'Talla Equipación': futbolista.talla_equipacion || 'No especificada',
        'Tutor': futbolista.tutor?.nombre || '',
        'Email Tutor': futbolista.tutor?.email || '',
        'Teléfono Tutor': futbolista.tutor?.telefono || '',
        'Fecha Inscripción': futbolista.fecha_inscripcion ?
          new Date(futbolista.fecha_inscripcion).toLocaleDateString('es-ES') : '',
        'Invitado': futbolista.invitado ? 'Sí' : 'No',
        'Saldo Jornadas': futbolista.saldo_jornadas || 0
      }));

      // Crear el libro de Excel
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();

      // Añadir la hoja al libro
      XLSX.utils.book_append_sheet(workbook, worksheet, `Debutantes J${jornadaNumero}`);

      // Configurar el ancho de las columnas
      const columnWidths = [
        { wch: 20 }, // Torneo
        { wch: 8 },  // Jornada
        { wch: 12 }, // Fecha Jornada
        { wch: 8 },  // Hora Jornada
        { wch: 20 }, // Sede
        { wch: 8 },  // ID Futbolista
        { wch: 25 }, // Nombre
        { wch: 15 }, // Fecha Nacimiento
        { wch: 8 },  // Edad
        { wch: 12 }, // Posición Principal
        { wch: 12 }, // Posición Secundaria
        { wch: 15 }, // Categoría
        { wch: 20 }, // Club
        { wch: 15 }, // Federación Club
        { wch: 15 }, // División
        { wch: 12 }, // Temporada
        { wch: 18 }, // Talla Equipación
        { wch: 25 }, // Tutor
        { wch: 30 }, // Email Tutor
        { wch: 15 }, // Teléfono Tutor
        { wch: 15 }, // Fecha Inscripción
        { wch: 8 },  // Invitado
        { wch: 8 }   // Saldo Jornadas
      ];
      worksheet['!cols'] = columnWidths;

      // Generar el nombre del archivo con fecha y hora
      const now = new Date();
      const timestamp = now.toISOString().slice(0, 19).replace(/:/g, '-');

      // Usar información real de la jornada para el nombre del archivo
      const torneoLimpio = jornadaInfo.torneo.replace(/\s+/g, '_');
      const sedeCorta = jornadaInfo.sede.replace(/\s+/g, '_').substring(0, 15);
      const fechaCorta = jornadaInfo.fecha.replace(/\//g, '-');

      const filename = `debutantes_${torneoLimpio}_jornada_${jornadaInfo.numero}_${sedeCorta}_${fechaCorta}_${timestamp}.xlsx`;

      // Descargar el archivo
      XLSX.writeFile(workbook, filename);

      toast.success(`✅ Excel exportado: ${futbolistasDebutantes.length} debutantes de la jornada ${jornadaInfo.numero} (${jornadaInfo.sede})`);

    } catch (error) {
      console.error('Error exportando futbolistas debutantes a Excel:', error);
      toast.error('Error al exportar los futbolistas debutantes a Excel');
    } finally {
      setLoading(false);
    }
  };

  // Función para manejar la vista de partidos organizados
  // preserveTab: si es true, no cambia la pestaña activa (útil para recargas desde Confirmados)
  const handleViewPartidosOrganizados = async (jornadaId, sedeId, preserveTab = false) => {
    setLoadingPartidos(true);
    setLoadingPropuestas(true);
    setError(null);
    try {
      console.log(`🏆 Cargando partidos para jornada ${jornadaId}, sede ${sedeId}`);

      // Cargar AMBOS en paralelo: confirmados y borradores
      const [confirmadosRes, borradoresRes] = await Promise.all([
        jornadaService.getPartidosOrganizados(jornadaId, sedeId),
        jornadaService.getPropuestaPartidos(jornadaId, torneoId)
      ]);

      // Partidos confirmados — backend devuelve { partidos: [...], total: N }
      if (confirmadosRes.partidos && confirmadosRes.partidos.length > 0) {
        setPartidosOrganizados(confirmadosRes.partidos);
      } else {
        setPartidosOrganizados([]);
      }

      // Borradores
      if (borradoresRes.success && borradoresRes.data && borradoresRes.data.length > 0) {
        setPropuestasPartidos(borradoresRes.data);
        if (!preserveTab) setShowPropuestas(true);
      } else {
        setPropuestasPartidos([]);
        if (!preserveTab) setShowPropuestas(false);
      }

      setShowPartidosOrganizados(true);
      setSelectedJornada(jornadaId);
      setShowJornadaDetail(false);
    } catch (error) {
      console.error('❌ Error al cargar partidos:', error);
      setPartidosOrganizados([]);
      setPropuestasPartidos([]);
      setShowPartidosOrganizados(true);
      setSelectedJornada(jornadaId);
      setShowJornadaDetail(false);
    } finally {
      setLoadingPartidos(false);
      setLoadingPropuestas(false);
    }
  };

  // Función para proponer partidos (basado en el organizador simplificado)
  const handleProponerPartidos = async () => {
    // Buscar la próxima jornada no disputada (o la seleccionada)
    let jornadaObjetivo = null;
    
    if (selectedJornada) {
      jornadaObjetivo = jornadas.find(j => j.jornada_id === selectedJornada);
    } else {
      jornadaObjetivo = jornadas.find(j => !j.disputada);
    }

    if (!jornadaObjetivo) {
      toast.error('No se encontró una jornada válida para proponer partidos');
      return;
    }

    setProponiendoPartidos(true);
    setError(null);
    try {
      console.log('📋 Proponiendo partidos para jornada:', jornadaObjetivo.jornada_id);
      
      const response = await jornadaService.proponerPartidos({
        jornadaId: jornadaObjetivo.jornada_id,
        torneoId: torneoId,
        temporadaId: torneo?.temporada || 2 // Fallback a 2 si no viene
      });

      if (response.success) {
        toast.success('¡Propuesta de partidos generada!');
        // En lugar de llamar a handleViewPropuestas, llamamos a handleViewPartidosOrganizados
        // que ahora ya sabe cargar propuestas si no hay oficiales.
        const jornadaActual = jornadas.find(j => j.jornada_id === jornadaObjetivo.jornada_id);
        if (jornadaActual) {
          await handleViewPartidosOrganizados(jornadaObjetivo.jornada_id, jornadaActual.sede_id);
        }
      } else {
        throw new Error(response.message || 'Error al generar propuesta');
      }
    } catch (error) {
      console.error('❌ Error al proponer partidos:', error);
      const errorMessage = error.response?.data?.message || error.message;
      setError(`Error al proponer partidos: ${errorMessage}`);
      toast.error(`Error al proponer partidos: ${errorMessage}`);
    } finally {
      setProponiendoPartidos(false);
    }
  };

  // Función para limpiar partidos organizados
  const handleLimpiarPartidos = async (jornadaId, sedeId) => {
    // Confirmar acción
    if (!window.confirm('¿Estás seguro de que quieres eliminar todos los partidos organizados de esta jornada? Esta acción no se puede deshacer.')) {
      return;
    }

    setLimpiandoPartidos(true);
    setError(null);
    try {
      console.log(`🧹 Limpiando partidos para jornada ${jornadaId}, sede ${sedeId}`);
      const response = await jornadaService.limpiarPartidosOrganizados(jornadaId, sedeId);
      console.log('✅ Respuesta limpiar partidos:', response);

      if (response.success) {
        // Mostrar mensaje de éxito
        toast.success(`¡Partidos eliminados exitosamente! Se eliminaron ${response.partidos_eliminados} partidos.`);

        // Recargar los partidos organizados (debería mostrar vacío)
        await handleViewPartidosOrganizados(jornadaId, sedeId);
        // (ELIMINADO) Recargar no asignados
      } else {
        throw new Error(response.message || 'Error desconocido');
      }
    } catch (error) {
      console.error('❌ Error al limpiar partidos:', error);
      const errorMessage = error.response?.data?.message || error.message;
      setError(`Error al limpiar partidos: ${errorMessage}`);
      toast.error(`Error al limpiar partidos: ${errorMessage}`);
    } finally {
      setLimpiandoPartidos(false);
    }
  };

  // (ELIMINADO) Función para obtener futbolistas no asignados

  // (ELIMINADO) Función para manejar la asignación de futbolista

  // (ELIMINADO) Función para cargar equipos disponibles

  // Función para cargar posiciones del futbolista (reservada para futuras funcionalidades)
  const _loadPosicionesFutbolista = async (futbolistaId) => {
    setLoadingPosiciones(true);
    try {
      const response = await futbolistaEnEquipoService.getPosicionesFutbolista(futbolistaId);
      if (response.success) {
        setPosicionesDisponibles(response.data.posiciones || []);
        // Seleccionar automáticamente la posición principal si existe
        const posicionPrincipal = response.data.posiciones?.find(p => p.tipo === 'principal');
        if (posicionPrincipal) {
          setPosicionSeleccionada(posicionPrincipal.id);
        }
      } else {
        console.error('Error al cargar posiciones:', response.error);
        setPosicionesDisponibles([]);
      }
    } catch (error) {
      console.error('Error al cargar posiciones del futbolista:', error);
      setPosicionesDisponibles([]);
    } finally {
      setLoadingPosiciones(false);
    }
  };

  // Función para ejecutar la asignación
  const ejecutarAsignacion = async (equipoId) => {
    if (!futbolistaSeleccionado || !equipoId) return;

    setAsignandoJugador(true);
    try {
      const response = await futbolistaEnEquipoService.asignarJugador({
        futbolista_id: futbolistaSeleccionado.futbolista_id,
        equipo_id: equipoId,
        posicion_asignada: posicionSeleccionada,
        es_titular: false // Por defecto no titular
      });

      if (response.success) {
        toast.success(response.message || 'Jugador asignado exitosamente');
        setShowAsignacionModal(false);

        // Recargar datos
        const jornadaActual = jornadas.find(j => j.jornada_id === selectedJornada);
        if (jornadaActual) {
          await handleViewPartidosOrganizados(selectedJornada, jornadaActual.sede_id);
        }
      } else {
        toast.error(response.error || 'Error al asignar jugador');
      }
    } catch (error) {
      console.error('Error al asignar jugador:', error);
      toast.error('Error al asignar jugador');
    } finally {
      setAsignandoJugador(false);
    }
  };

  // Función para cerrar modal de asignación
  const handleCloseAsignacionModal = () => {
    setShowAsignacionModal(false);
    setFutbolistaSeleccionado(null);
    setEquiposDisponibles([]);
    setPosicionesDisponibles([]);
    setPosicionSeleccionada(null);
  };

  // Función para recargar datos de partidos organizados (preserva la pestaña activa)
  const reloadPartidosOrganizados = async () => {
    if (selectedJornada) {
      const jornadaActual = jornadas.find(j => j.jornada_id === selectedJornada);
      if (jornadaActual) {
        await handleViewPartidosOrganizados(selectedJornada, jornadaActual.sede_id, true);
      }
    }
  };

  // Función para enviar notificación dentro de la app
  const handleSendAppNotification = async () => {
    const jornadaInfo = jornadas.find(j => j.jornada_id === selectedJornada);

    const confirmMessage = `¿Enviar notificación in-app a ${futbolistasNoInscritos.length} tutores?\n\n` +
      `📱 NOTIFICACIÓN IN-APP:\n` +
      `• Se enviará a la sección "/notifications" bajo "Inscripciones"\n` +
      `• Jornada: ${jornadaInfo?.jornada_numero}\n` +
      `• Destinatarios: ${futbolistasNoInscritos.length} tutores\n` +
      `• Mensaje: "¡Aún estás a tiempo de inscribir a tu futbolista!"\n\n` +
      `¿Continuar?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setSendingNotification(true);
    try {
      const result = await notificationService.sendJornadaReminder(selectedJornada, 'app');

      setError(null);

      // Mostrar mensaje de éxito
      alert(`✅ Notificación in-app enviada exitosamente a ${result.successCount} tutores${result.errorCount > 0 ? ` (${result.errorCount} errores)` : ''}`);
    } catch (error) {
      console.error('Error al enviar notificación:', error);
      setError('Error al enviar notificación in-app');
    } finally {
      setSendingNotification(false);
    }
  };

  // Función para enviar email
  const handleSendEmail = async () => {
    const jornadaInfo = jornadas.find(j => j.jornada_id === selectedJornada);

    const confirmMessage = `¿Enviar email a ${futbolistasNoInscritos.length} tutores?\n\n` +
      `📧 EMAIL PERSONALIZADO:\n` +
      `• Se enviará un email HTML completo a cada tutor\n` +
      `• Asunto: "⚽ ¡Última oportunidad! Inscripción Jornada ${jornadaInfo?.jornada_numero} abierta"\n` +
      `• Incluye: Detalles de la jornada, información del futbolista, botón de acción\n` +
      `• Destinatarios: ${futbolistasNoInscritos.length} tutores con email válido\n` +
      `• Mensaje: "¡Aún estás a tiempo de inscribir a tu futbolista!"\n\n` +
      `¿Continuar?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setSendingNotification(true);
    try {
      const result = await notificationService.sendJornadaReminder(selectedJornada, 'email');

      setError(null);

      // Mostrar mensaje de éxito
      alert(`📧 Email enviado exitosamente a ${result.successCount} tutores${result.errorCount > 0 ? ` (${result.errorCount} errores)` : ''}`);
    } catch (error) {
      console.error('Error al enviar email:', error);
      setError('Error al enviar emails');
    } finally {
      setSendingNotification(false);
    }
  };

  // Función para confirmar jornada
  const handleConfirmarJornada = async (jornadaId, jornadaNumero) => {
    const confirmMessage = `¿Confirmar jornada ${jornadaNumero}?\n\n` +
      `Esta acción:\n` +
      `✅ Marcará la jornada como disputada\n` +
      `✅ Identificará futbolistas inscritos que NO jugaron\n` +
      `✅ Los reasignará automáticamente a la siguiente jornada disponible\n\n` +
      `¿Continuar?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      console.log(`🔄 Confirmando jornada ${jornadaId}...`);
      const response = await jornadaService.confirmarJornada(jornadaId);
      console.log('✅ Respuesta confirmar jornada:', response);

      if (response.success) {
        toast.success(response.message);

        // Mostrar detalles si hay futbolistas reasignados
        if (response.data.futbolistas_reasignados > 0) {
          toast.info(
            `📋 ${response.data.futbolistas_no_jugaron} futbolista(s) no jugaron\n` +
            `✅ ${response.data.futbolistas_reasignados} reasignado(s) a la siguiente jornada`,
            { autoClose: 5000 }
          );
        }

        // Recargar datos
        await loadData();
      } else {
        toast.error(response.message || 'Error al confirmar jornada');
      }
    } catch (error) {
      console.error('❌ Error al confirmar jornada:', error);
      toast.error(error.response?.data?.message || 'Error al confirmar la jornada');
    } finally {
      setLoading(false);
    }
  };

  // Renderizar loading
  if (loading) {
    return (
      <AdminLayout>
        <Container fluid className="py-4">
          <div className="text-center">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Cargando...</span>
            </Spinner>
            <p className="mt-3">Cargando futbolistas inscritos...</p>
          </div>
        </Container>
      </AdminLayout>
    );
  }

  // Renderizar error
  if (error) {
    return (
      <AdminLayout>
        <Container fluid className="py-4">
          <Alert variant="danger">
            <Alert.Heading>Error</Alert.Heading>
            <p>{error}</p>
            <div className="d-flex gap-2">
              <Button variant="outline-danger" onClick={loadData}>
                Reintentar
              </Button>
              <Button variant="secondary" onClick={handleGoBack}>
                <FaArrowLeft className="me-2" />
                Volver
              </Button>
            </div>
          </Alert>
        </Container>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Page Header */}
      <div className="page-header mb-4 bg-light border-bottom py-3">
        <Container fluid style={{ maxWidth: '100%', padding: '0 3rem' }}>
          <Row>
            <Col>
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <Button
                    variant="outline-secondary"
                    onClick={handleGoBack}
                    className="me-3"
                  >
                    <FaArrowLeft />
                  </Button>
                  <div>
                    <h4 className="mb-1 text-primary">
                      <FaTrophy className="me-2" />
                      {torneo?.nombre || 'Torneo'}
                    </h4>
                    <p className="text-muted mb-0">
                      <FaUsers className="me-2" />
                      {futbolistas.length} futbolistas inscritos • {jornadas.length} jornadas
                    </p>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <div className="container-fluid">
        {/* Tabs de navegación */}
        <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
          <div className="card mb-3">
            <div className="card-header bg-white border-bottom p-0">
              <div className="d-flex align-items-center">
                <style>
                  {`
                    .nav-tabs .nav-link {
                      text-decoration: none !important;
                      border: none !important;
                    }
                    .nav-tabs .nav-link:hover,
                    .nav-tabs .nav-link:focus,
                    .nav-tabs .nav-link:active,
                    .nav-tabs .nav-link.active {
                      text-decoration: none !important;
                      border: none !important;
                    }
                  `}
                </style>
                <Nav variant="tabs" className="border-0 flex-grow-1">
                <Nav.Item>
                  <Nav.Link
                    eventKey="futbolistas"
                    className="px-4 py-3"
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none !important',
                      borderRadius: '0',
                      borderBottom: activeTab === 'futbolistas' ? '3px solid #007bff' : '3px solid transparent',
                      color: activeTab === 'futbolistas' ? '#007bff' : '#6c757d',
                      textDecoration: 'none !important',
                      outline: 'none',
                      boxShadow: 'none'
                    }}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'none'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                  >
                    <FaUsers className="me-2" />
                    Futbolistas Inscritos ({futbolistas.length})
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    eventKey="jornadas"
                    className="px-4 py-3"
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none !important',
                      borderRadius: '0',
                      borderBottom: activeTab === 'jornadas' ? '3px solid #007bff' : '3px solid transparent',
                      color: activeTab === 'jornadas' ? '#007bff' : '#6c757d',
                      textDecoration: 'none !important',
                      outline: 'none',
                      boxShadow: 'none'
                    }}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'none'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                  >
                    <FaCalendarAlt className="me-2" />
                    Jornadas ({jornadas.length})
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              {/* Botón de exportación Excel */}
              <div className="ms-auto pe-3 d-flex align-items-center">
                <Button
                  variant="success"
                  size="sm"
                  className="d-flex align-items-center"
                  onClick={exportFutbolistasInscritosPorJornada}
                  disabled={loading}
                  title="Exportar todos los futbolistas inscritos a Excel"
                  style={{ fontSize: '13px', padding: '8px 16px' }}
                >
                  <FaFileExcel className="me-2" size={14} />
                  Excel Inscritos
                </Button>
              </div>
              </div>
            </div>
          </div>

          <Tab.Content>
            <Tab.Pane eventKey="futbolistas">
              <div className="py-3">
                {futbolistas.length === 0 ? (
                  <div className="text-center py-5">
                    <FaUsers className="text-muted mb-3" size={48} />
                    <h5 className="text-muted">No hay futbolistas inscritos</h5>
                    <p className="text-muted">Este torneo aún no tiene futbolistas inscritos.</p>
                  </div>
                ) : (
                  <>
                    {/* Información de paginación */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="text-muted">
                        Mostrando {Math.min((currentFutbolistaPage - 1) * futbolistasPerPage + 1, futbolistas.length)} - {Math.min(currentFutbolistaPage * futbolistasPerPage, futbolistas.length)} de {futbolistas.length} futbolistas
                      </div>
                    </div>

                    <div className="row g-3">
                      {(() => {
                        // Calcular futbolistas para la página actual
                        const indexOfLastFutbolista = currentFutbolistaPage * futbolistasPerPage;
                        const indexOfFirstFutbolista = indexOfLastFutbolista - futbolistasPerPage;
                        const currentFutbolistas = futbolistas.slice(indexOfFirstFutbolista, indexOfLastFutbolista);

                        return currentFutbolistas.map((futbolista) => (
                          <div key={futbolista.id} className="col-12 col-sm-6">
                            <div className="card shadow-sm h-100">
                              <div className="card-body p-3 d-flex flex-column">
                                {/* Header del futbolista */}
                                <div className="d-flex align-items-start mb-3">
                                  <div className="player-avatar me-3 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '45px', height: '45px', fontSize: '18px', fontWeight: 'bold', flexShrink: 0}}>
                                    {(futbolista.nombre || 'F').charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-grow-1">
                                    <h6 className="card-title mb-1 fw-bold">{futbolista.nombre}</h6>
                                    <p className="card-text text-muted small mb-1">
                                      {calculateAge(futbolista.fecha_nacimiento)} años
                                    </p>
                                    <span className="badge bg-secondary small">
                                      {formatDate(futbolista.fecha_nacimiento)}
                                    </span>
                                  </div>
                                </div>

                                {/* Información del futbolista */}
                                <div className="mb-2">
                                  <div className="small text-muted">
                                    <div className="mb-1">
                                      <strong>⚽ Posición Principal:</strong> {
                                        futbolista.posicion_principal ||
                                        (futbolista.futbolistaTemporada && futbolista.futbolistaTemporada.demarcacion1 && futbolista.futbolistaTemporada.demarcacion1.posicion) ||
                                        'No especificada'
                                      }
                                    </div>
                                    {(futbolista.posicion_secundaria || (futbolista.futbolistaTemporada && futbolista.futbolistaTemporada.demarcacion2 && futbolista.futbolistaTemporada.demarcacion2.posicion)) && (
                                      <div className="mb-1">
                                        <strong>🔄 Posición Secundaria:</strong> {
                                          futbolista.posicion_secundaria ||
                                          (futbolista.futbolistaTemporada && futbolista.futbolistaTemporada.demarcacion2 && futbolista.futbolistaTemporada.demarcacion2.posicion)
                                        }
                                      </div>
                                    )}
                                    {futbolista.club && (
                                      <div className="mb-1">
                                        <strong>🏟️ Club:</strong> {futbolista.club.nombre}
                                      </div>
                                    )}
                                    {futbolista.division && (
                                      <div className="mb-1">
                                        <strong>📊 División:</strong> {futbolista.division.nombre}
                                      </div>
                                    )}
                                    {futbolista.sede_preferida && (
                                      <div className="mb-1">
                                        <strong>📍 Sede Preferida:</strong> {futbolista.sede_preferida.nombre}
                                      </div>
                                    )}
                                    {futbolista.inscripcion && (
                                      <div className="mb-1">
                                        <strong>💰 Saldo:</strong> {futbolista.inscripcion.saldo_jornadas || 0} jornadas
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>

                    {/* Paginación */}
                    {futbolistas.length > futbolistasPerPage && (
                      <div className="d-flex justify-content-center align-items-center mt-4 gap-2">
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => setCurrentFutbolistaPage(currentFutbolistaPage - 1)}
                          disabled={currentFutbolistaPage === 1}
                        >
                          ← Anterior
                        </button>

                        <span className="text-muted small mx-2">
                          Página {currentFutbolistaPage} de {Math.ceil(futbolistas.length / futbolistasPerPage)}
                        </span>

                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => setCurrentFutbolistaPage(currentFutbolistaPage + 1)}
                          disabled={currentFutbolistaPage === Math.ceil(futbolistas.length / futbolistasPerPage)}
                        >
                          Siguiente →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Tab.Pane>
            <Tab.Pane eventKey="jornadas">
              <div className="py-3">
                {jornadas.length === 0 ? (
                          <div className="text-center py-5">
                            <FaCalendarAlt className="text-muted mb-3" size={48} />
                            <h5 className="text-muted">No hay jornadas programadas</h5>
                            <p className="text-muted">Este torneo aún no tiene jornadas programadas.</p>
                          </div>
                        ) : !showJornadaDetail && !showNoInscritos && !showPartidosOrganizados ? (
                          /* Vista Grid - Similar a /matches con paginación */
                          <>
                            <div className="row g-3">
                              {(() => {
                                // Calcular jornadas para la página actual
                                const indexOfLastJornada = currentJornadaPage * jornadasPerPage;
                                const indexOfFirstJornada = indexOfLastJornada - jornadasPerPage;
                                const currentJornadas = jornadas.slice(indexOfFirstJornada, indexOfLastJornada);

                                return (
                                  <>
                                    {currentJornadas.map((jornada) => (
                                      <div key={jornada.jornada_id} className="col-12 col-md-6 col-lg-4 mb-3">
                                        <div className="card shadow-sm h-100" style={{cursor: 'pointer'}} onClick={() => {
                                          setSelectedJornada(jornada.jornada_id);
                                          setShowJornadaDetail(true);
                                        }}>
                                          <div className="card-body p-3 d-flex flex-column">
                                            {/* Header de la jornada */}
                                            <div className="d-flex align-items-start mb-3">
                                              <div className="me-3 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '45px', height: '45px', fontSize: '18px', fontWeight: 'bold', flexShrink: 0}}>
                                                {jornada.jornada_numero}
                                              </div>
                                              <div className="flex-grow-1">
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                  <h6 className="card-title mb-0 fw-bold">Jornada {jornada.jornada_numero}</h6>
                                                  {/* Estado compacto */}
                                                  <div className={`${jornada.disputada ? 'bg-success' : 'bg-warning'} text-white rounded px-2 py-1 small fw-bold`}>
                                                    {jornada.disputada ? '✅ Disputada' : '⏱ Programada'}
                                                  </div>
                                                </div>
                                                <p className="card-text text-muted small mb-0">
                                                  {jornada.sede_nombre}
                                                </p>
                                              </div>
                                            </div>

                                            {/* Información básica de la jornada */}
                                            <div className="mb-3">
                                              <div className="small text-muted">
                                                <div className="mb-1">
                                                  <strong>📅 Fecha:</strong> {formatDate(jornada.jornada_fecha)}
                                                </div>
                                                <div className="mb-1">
                                                  <strong>🕐 Hora:</strong> {jornada.jornada_hora}
                                                </div>
                                                <div className="mb-1">
                                                  <strong>📍 Ubicación:</strong> {jornada.sede_poblacion || 'No especificada'}
                                                </div>
                                              </div>
                                            </div>

                                            {/* Resumen de inscritos */}
                                            {jornada.futbolistas_inscritos && jornada.futbolistas_inscritos.length > 0 && (
                                              <div className="mb-2">
                                                <div className="bg-primary text-white rounded p-2 small">
                                                  {(() => {
                                                    const resumen = generateJornadaInscritosResumen(jornada);
                                                    const anosOrdenados = Object.keys(resumen.porAno)
                                                      .filter(ano => ano !== 'Sin año')
                                                      .sort((a, b) => parseInt(b) - parseInt(a));

                                                    return (
                                                      <div>
                                                        {/* Total */}
                                                        <div className="fw-bold mb-1 text-center">
                                                          📊 {resumen.total} inscritos
                                                        </div>

                                                        {/* Por año - una línea */}
                                                        <div className="mb-1">
                                                          <strong>🎂 Años: </strong>
                                                          {anosOrdenados.map((ano, index) => (
                                                            <span key={ano}>
                                                              {ano}({resumen.porAno[ano]})
                                                              {index < anosOrdenados.length - 1 ? ', ' : ''}
                                                            </span>
                                                          ))}
                                                          {resumen.porAno['Sin año'] && (
                                                            <span>, Sin año({resumen.porAno['Sin año']})</span>
                                                          )}
                                                        </div>

                                                        {/* Todas las posiciones */}
                                                        <div>
                                                          <strong>⚽ Posiciones: </strong>
                                                          {(() => {
                                                            const todasPosiciones = {};
                                                            Object.values(resumen.porAnoPosicion).forEach(posiciones => {
                                                              Object.entries(posiciones).forEach(([pos, cant]) => {
                                                                todasPosiciones[pos] = (todasPosiciones[pos] || 0) + cant;
                                                              });
                                                            });

                                                            // Ordenar posiciones: Portero primero, luego por cantidad
                                                            const posicionesOrdenadas = Object.entries(todasPosiciones)
                                                              .sort(([posA, cantA], [posB, cantB]) => {
                                                                // Portero siempre primero
                                                                if (posA.toLowerCase().includes('portero')) return -1;
                                                                if (posB.toLowerCase().includes('portero')) return 1;
                                                                // Luego por cantidad (mayor a menor)
                                                                return cantB - cantA;
                                                              });

                                                            return posicionesOrdenadas.map(([pos, cant], index, arr) => (
                                                              <span key={pos}>
                                                                {pos}({cant})
                                                                {index < arr.length - 1 ? ', ' : ''}
                                                              </span>
                                                            ));
                                                          })()}
                                                        </div>
                                                      </div>
                                                    );
                                                  })()}
                                                </div>
                                              </div>
                                            )}

                                            {/* Mensaje si no hay inscritos */}
                                            {(!jornada.futbolistas_inscritos || jornada.futbolistas_inscritos.length === 0) && (
                                              <div className="mb-3">
                                                <div className="bg-light rounded p-2 text-center">
                                                  <div className="small text-muted">Sin inscritos</div>
                                                </div>
                                              </div>
                                            )}

                                            {/* Botones de acción */}
                                            <div className="d-flex justify-content-end gap-1 pt-2 border-top mt-auto">
                                              <Button
                                                variant="outline-warning"
                                                size="sm"
                                                className="p-1 d-flex align-items-center justify-content-center"
                                                style={{width: '32px', height: '32px'}}
                                                title="Editar jornada"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleEditarJornada(jornada);
                                                }}
                                                disabled={jornada.disputada}
                                              >
                                                <FaEdit size={14} />
                                              </Button>
                                              <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="p-1 d-flex align-items-center justify-content-center"
                                                style={{width: '32px', height: '32px'}}
                                                title="Ver no inscritos"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  loadFutbolistasNoInscritos(jornada.jornada_id);
                                                }}
                                                disabled={loadingNoInscritos}
                                              >
                                                <FaUserSlash size={14} />
                                              </Button>
                                              <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="p-1 d-flex align-items-center justify-content-center"
                                                style={{width: '32px', height: '32px'}}
                                                title="Ver inscritos"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setSelectedJornada(jornada.jornada_id);
                                                  setShowJornadaDetail(true);
                                                }}
                                              >
                                                <FaEye size={14} />
                                              </Button>
                                              <Button
                                                variant="outline-success"
                                                size="sm"
                                                className="p-1 d-flex align-items-center justify-content-center"
                                                style={{width: '32px', height: '32px'}}
                                                title="Ver partidos organizados"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleViewPartidosOrganizados(jornada.jornada_id, jornada.sede_id);
                                                }}
                                              >
                                                <FaFutbol size={14} />
                                              </Button>
                                              <Button
                                                variant="outline-info"
                                                size="sm"
                                                className="p-1 d-flex align-items-center justify-content-center"
                                                style={{width: '32px', height: '32px'}}
                                                title={`Exportar inscritos de jornada ${jornada.numero} a Excel`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  exportFutbolistasJornadaEspecifica(
                                                    jornada.jornada_id,
                                                    jornada.numero
                                                  );
                                                }}
                                                disabled={loading}
                                              >
                                                <FaFileExcel size={14} />
                                              </Button>
                                              <Button
                                                variant="outline-warning"
                                                size="sm"
                                                className="p-1 d-flex align-items-center justify-content-center"
                                                style={{width: '32px', height: '32px'}}
                                                title={`Exportar debutantes de jornada ${jornada.numero} a Excel (con talla equipación)`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  exportFutbolistasDebutantesJornadaEspecifica(
                                                    jornada.jornada_id,
                                                    jornada.numero
                                                  );
                                                }}
                                                disabled={loading}
                                              >
                                                <FaUserPlus size={14} />
                                              </Button>
                                              <Button
                                                variant={jornada.disputada ? "success" : "outline-danger"}
                                                size="sm"
                                                className="p-1 d-flex align-items-center justify-content-center"
                                                style={{width: '32px', height: '32px'}}
                                                title={jornada.disputada ? "Jornada ya confirmada" : `Confirmar jornada ${jornada.numero}`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleConfirmarJornada(
                                                    jornada.jornada_id,
                                                    jornada.numero
                                                  );
                                                }}
                                                disabled={jornada.disputada || loading}
                                              >
                                                <FaCheckCircle size={14} />
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </>
                                );
                              })()}
                            </div>

                            {/* Paginación */}
                            {jornadas.length > jornadasPerPage && (
                              <div className="d-flex justify-content-center align-items-center mt-4 gap-2">
                                {/* Botón Anterior */}
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => setCurrentJornadaPage(prev => Math.max(1, prev - 1))}
                                  disabled={currentJornadaPage === 1}
                                  style={{
                                    fontSize: '12px',
                                    padding: '4px 10px',
                                    minWidth: '36px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <FaChevronLeft size={10} />
                                </Button>

                                {/* Página Anterior (si existe) */}
                                {currentJornadaPage > 1 && (
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => setCurrentJornadaPage(currentJornadaPage - 1)}
                                    style={{
                                      fontSize: '12px',
                                      padding: '4px 10px',
                                      minWidth: '36px'
                                    }}
                                  >
                                    {currentJornadaPage - 1}
                                  </Button>
                                )}

                                {/* Página Actual */}
                                <Button
                                  variant="primary"
                                  size="sm"
                                  style={{
                                    fontSize: '12px',
                                    padding: '4px 10px',
                                    minWidth: '36px',
                                    fontWeight: '600'
                                  }}
                                >
                                  {currentJornadaPage}
                                </Button>

                                {/* Página Siguiente (si existe) */}
                                {currentJornadaPage < Math.ceil(jornadas.length / jornadasPerPage) && (
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => setCurrentJornadaPage(currentJornadaPage + 1)}
                                    style={{
                                      fontSize: '12px',
                                      padding: '4px 10px',
                                      minWidth: '36px'
                                    }}
                                  >
                                    {currentJornadaPage + 1}
                                  </Button>
                                )}

                                {/* Botón Siguiente */}
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => setCurrentJornadaPage(prev => Math.min(Math.ceil(jornadas.length / jornadasPerPage), prev + 1))}
                                  disabled={currentJornadaPage === Math.ceil(jornadas.length / jornadasPerPage)}
                                  style={{
                                    fontSize: '12px',
                                    padding: '4px 10px',
                                    minWidth: '36px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <FaChevronRight size={10} />
                                </Button>
                              </div>
                            )}
                          </>
                        ) : showJornadaDetail ? (
                          /* Vista de detalle de jornada con futbolistas inscritos */
                          (() => {
                          const jornadaSeleccionada = jornadas.find(j => j.jornada_id === selectedJornada);

                          if (!jornadaSeleccionada) return null;

                          const futbolistasInscritos = jornadaSeleccionada.futbolistas_inscritos || [];

                          return (
                            <div className="mt-4">
                              {/* Botón para volver */}
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setShowJornadaDetail(false)}
                                className="mb-3"
                                style={{ fontSize: '13px', padding: '6px 12px' }}
                              >
                                <FaChevronLeft className="me-1" size={12} />
                                Volver a Jornadas
                              </Button>

                              {/* Información de la jornada */}
                              <div className="card mb-4" style={{ border: '3px solid #0d6efd', borderRadius: '8px', boxShadow: '0 4px 12px rgba(13,110,253,0.2)' }}>
                                <div className="card-header bg-primary text-white" style={{ padding: '20px' }}>
                                  <div className="d-flex justify-content-between align-items-center gap-2">
                                    <div>
                                      <h4 className="mb-1" style={{ fontSize: '22px', fontWeight: '700' }}>
                                        Jornada {jornadaSeleccionada.jornada_numero}
                                      </h4>
                                      <div style={{ fontSize: '14px', opacity: 0.9 }}>
                                        <FaCalendarAlt className="me-2" size={14} />
                                        {formatDate(jornadaSeleccionada.jornada_fecha)} a las {jornadaSeleccionada.jornada_hora}
                                      </div>
                                    </div>
                                    <span
                                      style={{
                                        fontSize: '12px',
                                        padding: '6px 12px',
                                        fontWeight: '600',
                                        whiteSpace: 'nowrap',
                                        backgroundColor: jornadaSeleccionada.disputada ? '#28a745' : 'rgba(255,255,255,0.25)',
                                        color: 'white',
                                        borderRadius: '6px',
                                        display: 'inline-block'
                                      }}
                                    >
                                      {jornadaSeleccionada.disputada ? '✓ Disputada' : '⏱ Programada'}
                                    </span>
                                  </div>
                                </div>
                                <div className="card-body" style={{ padding: '20px', backgroundColor: '#f8f9fa' }}>
                                  <div className="d-flex align-items-center" style={{ fontSize: '15px' }}>
                                    <FaMapMarkerAlt className="me-2 text-primary" size={18} />
                                    <strong className="me-2">Sede:</strong>
                                    <span>{jornadaSeleccionada.sede_nombre}, {jornadaSeleccionada.sede_poblacion}</span>
                                  </div>
                                </div>
                              </div>

                              {/* Título de futbolistas */}
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0" style={{ fontSize: '18px', fontWeight: '600' }}>
                                  <FaUsers className="me-2 text-primary" size={18} />
                                  Futbolistas Inscritos
                                </h5>
                                <Badge bg="primary" style={{ fontSize: '14px', padding: '8px 16px' }}>
                                  {futbolistasInscritos.length} {futbolistasInscritos.length === 1 ? 'futbolista' : 'futbolistas'}
                                </Badge>
                              </div>

                              {/* Grid de tarjetas de futbolistas */}
                              {futbolistasInscritos.length === 0 ? (
                                <div className="text-center py-5">
                                  <FaUsers className="text-muted mb-3" size={48} />
                                  <h5 className="text-muted">No hay futbolistas inscritos</h5>
                                  <p className="text-muted">Esta jornada no tiene futbolistas inscritos aún.</p>
                                </div>
                              ) : (
                                <div className="row g-3">
                                  {futbolistasInscritos.map((futbolista, index) => (
                                    <div key={`${futbolista.futbolista_id}-${index}`} className="col-12 col-sm-6">
                                      <div className="card shadow-sm h-100">
                                        <div className="card-body p-3 d-flex flex-column">
                                          {/* Header del futbolista */}
                                          <div className="d-flex align-items-start mb-3">
                                            <div className="player-avatar me-3 bg-success text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '45px', height: '45px', fontSize: '18px', fontWeight: 'bold', flexShrink: 0}}>
                                              {(futbolista.futbolista_nombre || 'F').charAt(0).toUpperCase()}
                                            </div>
                                            <div className="flex-grow-1">
                                              <div className="d-flex align-items-center justify-content-between mb-2">
                                                <h6 className="card-title mb-0 fw-bold">{futbolista.futbolista_nombre}</h6>
                                                {/* Badge de estado */}
                                                <span className={`badge ${futbolista.inscripcion_estado === 1 ? 'bg-success' : 'bg-warning'} small`}>
                                                  {futbolista.inscripcion_estado === 1 ? '✅' : '⏳'}
                                                </span>
                                              </div>
                                              <p className="card-text text-muted small mb-0">
                                                {futbolista.futbolista_fecha_nacimiento
                                                  ? `Año ${new Date(futbolista.futbolista_fecha_nacimiento).getFullYear()}`
                                                  : 'Año no especificado'}
                                                {futbolista.division_nombre && ` • ${futbolista.division_nombre}`}
                                              </p>
                                            </div>
                                          </div>

                                          {/* Información del futbolista */}
                                          <div className="mb-3">
                                            <div className="small text-muted">
                                              <div className="mb-1">
                                                <strong>⚽ Posición:</strong> {futbolista.posicion_principal || 'No especificada'}
                                                {futbolista.posicion_secundaria && ` / ${futbolista.posicion_secundaria}`}
                                              </div>
                                              <div className="mb-1">
                                                <strong>🏟️ Club:</strong> {futbolista.club_nombre || 'Sin club'}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()
                        ) : showNoInscritos ? (
                          /* Vista de futbolistas NO inscritos */
                          <div className="mt-4">
                            {/* Botón para volver */}
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => {
                                setShowNoInscritos(false);
                                setFutbolistasNoInscritos([]);
                              }}
                              className="mb-3"
                              style={{ fontSize: '13px', padding: '6px 12px' }}
                            >
                              <FaChevronLeft className="me-1" size={12} />
                              Volver a Jornadas
                            </Button>

                            {/* Header */}
                            <div className="card mb-4" style={{ border: '3px solid #dc3545', borderRadius: '8px', boxShadow: '0 4px 12px rgba(220,53,69,0.2)' }}>
                              <div className="card-header bg-danger text-white" style={{ padding: '20px' }}>
                                <div className="d-flex justify-content-between align-items-center gap-3">
                                  <div>
                                    <h4 className="mb-1" style={{ fontSize: '22px', fontWeight: '700' }}>
                                      <FaUserSlash className="me-2" />
                                      Futbolistas NO Inscritos
                                    </h4>
                                    <div style={{ fontSize: '14px', opacity: 0.9 }}>
                                      Jornada {jornadas.find(j => j.jornada_id === selectedJornada)?.jornada_numero}
                                    </div>
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    <span
                                      style={{
                                        fontSize: '12px',
                                        backgroundColor: 'rgba(255,255,255,0.2)',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontWeight: '600'
                                      }}
                                    >
                                      {futbolistasNoInscritos.length} no inscritos
                                    </span>
                                    {futbolistasNoInscritos.length > 0 && (
                                      <div className="d-flex gap-1">
                                        <Button
                                          variant="outline-light"
                                          size="sm"
                                          onClick={handleSendAppNotification}
                                          disabled={sendingNotification}
                                          style={{
                                            fontSize: '11px',
                                            padding: '5px 8px',
                                            borderColor: 'rgba(255,255,255,0.5)',
                                            backgroundColor: 'rgba(255,255,255,0.1)'
                                          }}
                                          className="d-flex align-items-center gap-1"
                                        >
                                          <FaBell size={10} />
                                          {sendingNotification ? 'Enviando...' : 'App'}
                                        </Button>
                                        <Button
                                          variant="outline-light"
                                          size="sm"
                                          onClick={handleSendEmail}
                                          disabled={sendingNotification}
                                          style={{
                                            fontSize: '11px',
                                            padding: '5px 8px',
                                            borderColor: 'rgba(255,255,255,0.5)',
                                            backgroundColor: 'rgba(255,255,255,0.1)'
                                          }}
                                          className="d-flex align-items-center gap-1"
                                        >
                                          <FaEnvelope size={10} />
                                          {sendingNotification ? 'Enviando...' : 'Email'}
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Lista de futbolistas no inscritos */}
                            {loadingNoInscritos ? (
                              <div className="text-center py-5">
                                <Spinner animation="border" role="status" variant="danger">
                                  <span className="visually-hidden">Cargando...</span>
                                </Spinner>
                                <p className="mt-3">Cargando futbolistas no inscritos...</p>
                              </div>
                            ) : futbolistasNoInscritos.length === 0 ? (
                              <div className="text-center py-5">
                                <FaUserSlash className="text-success mb-3" size={48} />
                                <h5 className="text-success">¡Excelente! 🎉</h5>
                                <p className="text-muted">Todos los futbolistas del torneo están inscritos en esta jornada.</p>
                              </div>
                            ) : (
                              <div className="row g-3">
                                {futbolistasNoInscritos.map((futbolista) => (
                                  <div key={futbolista.futbolista_id} className="col-12 col-sm-6 col-lg-4">
                                    <div className="card shadow-sm h-100">
                                      <div className="card-body p-3 d-flex flex-column">
                                        {/* Header del futbolista */}
                                        <div className="d-flex align-items-start mb-3">
                                          <div className="player-avatar me-3 bg-danger text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '45px', height: '45px', fontSize: '18px', fontWeight: 'bold', flexShrink: 0}}>
                                            {(futbolista.futbolista_nombre || 'F').charAt(0).toUpperCase()}
                                          </div>
                                          <div className="flex-grow-1">
                                            <h6 className="card-title mb-1 fw-bold">{futbolista.futbolista_nombre}</h6>
                                            <p className="card-text text-muted small mb-1">
                                              {calculateAge(futbolista.futbolista_fecha_nacimiento)} años
                                            </p>
                                            <span className="badge bg-secondary small">
                                              {formatDate(futbolista.futbolista_fecha_nacimiento)}
                                            </span>
                                          </div>
                                        </div>

                                        {/* Información del futbolista */}
                                        <div className="mb-3">
                                          <div className="small text-muted">
                                            <div className="mb-1">
                                              <strong>⚽ Posición:</strong> {futbolista.posicion_principal || 'No especificada'}
                                              {futbolista.posicion_secundaria && ` / ${futbolista.posicion_secundaria}`}
                                            </div>
                                            <div className="mb-1">
                                              <strong>🏟️ Club:</strong> {futbolista.club_nombre || 'Sin club'}
                                            </div>
                                            <div className="mb-1">
                                              <strong>🏆 División:</strong> {futbolista.division_nombre || 'Sin división'}
                                            </div>
                                          </div>
                                        </div>

                                        {/* Información del tutor */}
                                        {futbolista.tutor && (
                                          <div className="mt-auto">
                                            <div className="border-top pt-2">
                                              <div className="small">
                                                <div className="fw-bold text-primary mb-1">👨‍👩‍👧‍👦 Tutor:</div>
                                                <div className="mb-1">
                                                  <strong>Nombre:</strong> {futbolista.tutor.nombre}
                                                </div>
                                                {futbolista.tutor.email && (
                                                  <div className="mb-1">
                                                    <strong>Email:</strong> {futbolista.tutor.email}
                                                  </div>
                                                )}
                                                {futbolista.tutor.telefono && (
                                                  <div>
                                                    <strong>Teléfono:</strong> {futbolista.tutor.telefono}
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : showPartidosOrganizados ? (
                          /* Vista de partidos organizados */
                          <div className="mt-4">
                            {/* Barra superior: Volver + Acciones */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => {
                                  setShowPartidosOrganizados(false);
                                  setPartidosOrganizados([]);
                                  setPropuestasPartidos([]);
                                }}
                                style={{ fontSize: '13px', padding: '6px 12px' }}
                              >
                                <FaChevronLeft className="me-1" size={12} />
                                Volver a Jornadas
                              </Button>

                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={handleProponerPartidos}
                                  disabled={proponiendoPartidos}
                                  style={{ fontSize: '13px', padding: '6px 12px' }}
                                >
                                  {proponiendoPartidos ? (
                                    <><Spinner animation="border" size="sm" className="me-1" /> Generando...</>
                                  ) : (
                                    <><FaRobot className="me-1" size={12} /> 📋 Propuesta Partidos</>
                                  )}
                                </Button>
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      const res = await jornadaService.crearBorradorVacio(selectedJornada, torneoId);
                                      if (res.success) {
                                        toast.success('Borrador vacío creado');
                                        const jornadaInfo = jornadas.find(j => j.jornada_id === selectedJornada);
                                        if (jornadaInfo) handleViewPartidosOrganizados(selectedJornada, jornadaInfo.sede_id);
                                        setShowPropuestas(true);
                                      }
                                    } catch (err) {
                                      toast.error('Error al crear borrador');
                                    }
                                  }}
                                  disabled={proponiendoPartidos}
                                  style={{ fontSize: '13px', padding: '6px 12px' }}
                                >
                                  <FaPlus className="me-1" size={12} />
                                  ➕ Añadir Partido Borrador
                                </Button>
                              </div>
                            </div>

                            {/* Header con info de jornada */}
                            <div className="card mb-3">
                              <div className="card-header py-2" style={{
                                background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                                color: 'white',
                                border: 'none'
                              }}>
                                <div className="d-flex align-items-center justify-content-between">
                                  <div className="d-flex align-items-center">
                                    <FaFutbol className="me-2" size={18} />
                                    <div>
                                      <h6 className="mb-0" style={{ fontWeight: '600' }}>
                                        Partidos — Jornada {jornadas.find(j => j.jornada_id === selectedJornada)?.jornada_numero || selectedJornada}
                                      </h6>
                                    </div>
                                  </div>
                                  <div></div>
                                </div>
                              </div>
                            </div>

                            {/* Pestañas: Borrador | Confirmados */}
                            <ul className="nav nav-tabs mb-3">
                              <li className="nav-item">
                                <button
                                  className={`nav-link ${showPropuestas ? 'active' : ''}`}
                                  onClick={() => setShowPropuestas(true)}
                                  style={{ fontSize: '14px' }}
                                >
                                  📋 Borrador {propuestasPartidos.length > 0 && <span className="text-muted">({propuestasPartidos.length})</span>}
                                </button>
                              </li>
                              <li className="nav-item">
                                <button
                                  className={`nav-link ${!showPropuestas ? 'active' : ''}`}
                                  onClick={() => setShowPropuestas(false)}
                                  style={{ fontSize: '14px' }}
                                >
                                  ✅ Confirmados {partidosOrganizados.length > 0 && <span className="text-muted">({partidosOrganizados.length})</span>}
                                </button>
                              </li>
                            </ul>

                            {/* Contenido de la pestaña activa */}
                            {showPropuestas ? (
                              propuestasPartidos.length > 0 ? (
                                <ProposedMatchesView
                                  propuestas={propuestasPartidos}
                                  setPropuestas={setPropuestasPartidos}
                                  loading={loadingPropuestas}
                                  jornadaId={selectedJornada}
                                  torneoId={torneoId}
                                  onConfirmed={() => {
                                    const jornadaInfo = jornadas.find(j => j.jornada_id === selectedJornada);
                                    if (jornadaInfo) handleViewPartidosOrganizados(selectedJornada, jornadaInfo.sede_id);
                                  }}
                                />
                              ) : (
                                <div className="text-center py-4 text-muted">
                                  <p>No hay borradores para esta jornada.</p>
                                  <small>Usa &ldquo;Propuesta Partidos&rdquo; para generar un borrador automático.</small>
                                </div>
                              )
                            ) : (
                              partidosOrganizados.length > 0 ? (
                                <>
                                  <div className="d-flex justify-content-end mb-3">
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => {
                                        const jornadaInfo = jornadas.find(j => j.jornada_id === selectedJornada);
                                        if (jornadaInfo) handleLimpiarPartidos(selectedJornada, jornadaInfo.sede_id);
                                      }}
                                      disabled={limpiandoPartidos}
                                      style={{ fontSize: '12px' }}
                                    >
                                      {limpiandoPartidos ? <><Spinner animation="border" size="sm" className="me-1" /> Limpiando...</> : '🧹 Limpiar Todos los Confirmados'}
                                    </Button>
                                  </div>
                                  <OrganizedMatchesView
                                    partidos={partidosOrganizados}
                                    loading={loadingPartidos}
                                    onDataChange={reloadPartidosOrganizados}
                                    onReload={reloadPartidosOrganizados}
                                    jornadaId={selectedJornada}
                                    sedeId={jornadas.find(j => j.jornada_id === selectedJornada)?.sede_id}
                                    onPlayerMove={(player, targetTeam) => console.log('Mover jugador:', player, 'a equipo:', targetTeam)}
                                    onPlayerRemove={(player) => console.log('Quitar jugador:', player)}
                                    onPlayerSubstitute={(player, substitute) => console.log('Sustituir:', player, 'por:', substitute)}
                                  />
                                </>
                              ) : (
                                <div className="text-center py-4 text-muted">
                                  <p>No hay partidos confirmados para esta jornada.</p>
                                  <small>Confirma partidos desde la pestaña &ldquo;Borrador&rdquo;.</small>
                                </div>
                              )
                            )}
                          </div>
                        ) : null}
              </div>
            </Tab.Pane>
            <Tab.Pane eventKey="old-jornadas-view" style={{ display: 'none' }}>
              <div className="py-3">
                        {jornadas.length === 0 ? (
                          <div className="text-center py-5">
                            <FaCalendarAlt className="text-muted mb-3" size={48} />
                            <h5 className="text-muted">No hay jornadas programadas</h5>
                            <p className="text-muted">Este torneo aún no tiene jornadas programadas.</p>
                          </div>
                        ) : (
                          <div className="row">
                            {jornadas.map((jornada) => (
                              <div key={jornada.jornada_id} className="col-12 mb-4">
                                <div className="card border-0 shadow-sm">
                                  <div className="card-header bg-light">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div>
                                        <h6 className="mb-1 text-primary">
                                          Jornada {jornada.jornada_numero}
                                        </h6>
                                        <div className="d-flex align-items-center gap-3">
                                          <small className="text-muted">
                                            <FaCalendarAlt className="me-1" />
                                            {formatDate(jornada.jornada_fecha)}
                                          </small>
                                          <small className="text-muted">
                                            <FaClock className="me-1" />
                                            {jornada.jornada_hora}
                                          </small>
                                          <small className="text-muted">
                                            <FaMapMarkerAlt className="me-1" />
                                            {jornada.sede_nombre}
                                          </small>
                                        </div>
                                      </div>
                                      <div className="d-flex align-items-center gap-2">
                                        <Badge bg={jornada.disputada ? 'success' : 'primary'}>
                                          {jornada.disputada ? (
                                            <>
                                              <FaCheckCircle className="me-1" />
                                              Disputada
                                            </>
                                          ) : (
                                            <>
                                              <FaClock className="me-1" />
                                              Programada
                                            </>
                                          )}
                                        </Badge>
                                        <Badge bg="secondary">
                                          {jornada.total_inscritos} inscritos
                                        </Badge>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="card-body">
                                    {jornada.futbolistas_inscritos && jornada.futbolistas_inscritos.length > 0 ? (
                                      <div className="row">
                                        {jornada.futbolistas_inscritos.map((futbolista, idx) => (
                                          <div key={`${jornada.jornada_id}-${futbolista.futbolista_id}-${idx}`} className="col-12 col-sm-6 mb-3">
                                            <div className="card shadow-sm h-100">
                                              <div className="card-body p-3 d-flex flex-column">
                                                {/* Header del futbolista */}
                                                <div className="d-flex align-items-start mb-2">
                                                  <div className="player-avatar me-3 bg-info text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '45px', height: '45px', fontSize: '18px', fontWeight: 'bold', flexShrink: 0}}>
                                                    {(futbolista.futbolista_nombre || 'F').charAt(0).toUpperCase()}
                                                  </div>
                                                  <div className="flex-grow-1">
                                                    <div className="d-flex align-items-center justify-content-between">
                                                      <h6 className="card-title mb-1 fw-bold">{futbolista.futbolista_nombre}</h6>
                                                      {/* Badge de estado */}
                                                      <span className={`badge ${futbolista.inscripcion_estado === 1 ? 'bg-success' : 'bg-warning'} small`}>
                                                        {futbolista.inscripcion_estado === 1 ? '✅' : '⏳'}
                                                      </span>
                                                    </div>
                                                    <p className="card-text text-muted small mb-0">
                                                      {calculateAge(futbolista.futbolista_fecha_nacimiento)} años
                                                    </p>
                                                  </div>
                                                </div>

                                                {/* Información del futbolista */}
                                                <div className="small text-muted">
                                                  {futbolista.posicion_principal && (
                                                    <div className="mb-1">
                                                      <strong>⚽ Posición:</strong> {futbolista.posicion_principal}
                                                    </div>
                                                  )}
                                                  {futbolista.club_nombre && (
                                                    <div className="mb-1">
                                                      <strong>🏟️ Club:</strong> {futbolista.club_nombre}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="text-center py-3">
                                        <FaUsers className="text-muted mb-2" size={24} />
                                        <p className="text-muted mb-0 small">No hay futbolistas inscritos en esta jornada</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
              </div>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>

      {/* Modal para asignar futbolista */}
      {showAsignacionModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 99999,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: window.innerWidth <= 768 ? '0.5rem' : '1rem'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCloseAsignacionModal();
            }
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: window.innerWidth <= 768 ? '0' : '0.5rem',
              width: window.innerWidth <= 768 ? '100vw' : '90vw',
              maxWidth: window.innerWidth <= 768 ? '100vw' : '600px',
              height: window.innerWidth <= 768 ? '100vh' : 'auto',
              maxHeight: window.innerWidth <= 768 ? '100vh' : '90vh',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                padding: '1rem',
                borderBottom: '1px solid #dee2e6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#f8f9fa'
              }}
            >
              <h5 style={{ margin: 0, fontWeight: 600 }}>
                Asignar Jugador - {futbolistaSeleccionado?.nombre}
              </h5>
              <button
                onClick={handleCloseAsignacionModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div
              style={{
                padding: '1rem',
                flex: 1,
                overflow: 'auto'
              }}
            >
              {_loadingEquipos ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Cargando equipos disponibles...
                </div>
              ) : equiposDisponibles.length > 0 ? (
                <div>
                  {/* Selector de posición */}
                  {loadingPosiciones ? (
                    <div className="mb-3">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Cargando posiciones...
                    </div>
                  ) : posicionesDisponibles.length > 0 ? (
                    <div className="mb-4">
                      <Form.Group>
                        <Form.Label><strong>Posición a asignar:</strong></Form.Label>
                        <Form.Select
                          value={posicionSeleccionada || ''}
                          onChange={(e) => setPosicionSeleccionada(parseInt(e.target.value))}
                          required
                        >
                          <option value="">Selecciona una posición</option>
                          {posicionesDisponibles.map((posicion) => (
                            <option key={posicion.id} value={posicion.id}>
                              {posicion.nombre} {posicion.tipo === 'principal' ? '(Principal)' : '(Secundaria)'}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </div>
                  ) : (
                    <Alert variant="warning" className="mb-3">
                      <FaExclamationTriangle className="me-2" />
                      No se encontraron posiciones para este jugador.
                    </Alert>
                  )}

                  <p className="mb-3">Selecciona un equipo para asignar al jugador:</p>
                  <div className="equipos-grid">
                    {equiposDisponibles.map((equipo) => (
                      <div
                        key={equipo.equipo_id}
                        className="equipo-option p-3 border rounded mb-2"
                        style={{
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: '#f8f9fa'
                        }}
                        onClick={() => {
                          if (!posicionSeleccionada) {
                            toast.error('Por favor selecciona una posición para el jugador');
                            return;
                          }
                          ejecutarAsignacion(equipo.equipo_id);
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#e9ecef';
                          e.target.style.borderColor = '#007bff';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#f8f9fa';
                          e.target.style.borderColor = '#dee2e6';
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <h6 className="mb-1">{equipo.nombre}</h6>
                            <small className="text-muted">
                              Partido {equipo.partido_id} • F{equipo.modalidad === 1 ? '8' : '11'} • {equipo.hora}
                            </small>
                          </div>
                          <div className="text-end">
                            <Badge bg="info">
                              {equipo.total_futbolistas} jugadores
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <Alert variant="info">
                  <FaExclamationTriangle className="me-2" />
                  No hay equipos disponibles para asignar.
                </Alert>
              )}

              {asignandoJugador && (
                <div className="text-center mt-3">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Asignando jugador...
                </div>
              )}
            </div>

            {/* Footer */}
            <div
              style={{
                padding: '1rem',
                borderTop: '1px solid #dee2e6',
                backgroundColor: '#f8f9fa',
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <Button
                variant="secondary"
                onClick={handleCloseAsignacionModal}
                disabled={asignandoJugador}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ManualMatchOrganizer eliminado — todo pasa por borrador */}

      {/* Modal para editar jornada */}
      <EditarJornadaInline
        show={showEditarJornadaModal}
        onHide={() => {
          setShowEditarJornadaModal(false);
          setJornadaParaEditar(null);
        }}
        jornada={jornadaParaEditar}
        onJornadaEditada={handleJornadaEditada}
      />
    </AdminLayout>
  );
};

export default TournamentPlayers;
